import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import SessionStore from '../../../stores/SessionStore';
import CargomaticInput from '../../v2/input';
import Card from '../../v2/card';
import ShipperSettings from './shipper/ShipperSettings';
import { getCustomerV2, updateShipper } from '../../../utils/CustomersAPIUtils';
import LoadingCircle from '../../ui/LoadingCircle';
const DEFAULT_APPLICATION_FEE_PERCENTAGE = 0.035;
export default function YardManagement() {
    const params = useParams();
    const history = useHistory();
    const id = params.id;
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [yardFreeDays, setYardFreeDays] = useState('');
    const [yardDemurrageRate, setYardDemurrageRate] = useState('');
    const [applicationFeePercentage, setApplicationFeePercentage] = useState(35);
    const [invoiceCompanyName, setInvoiceCompanyName] = useState('');
    const [invoiceAddress, setInvoiceAddress] = useState('');
    const [yardPerDiemFreeDays, setYardPerDiemFreeDays] = useState('');
    const [yardChassisRate, setYardChassisRate] = useState('');
    const [yardPerDiemRate, setYardPerDiemRate] = useState('');
    const [user, setUser] = useState();
    async function fetchUser(userId) {
        const user = await getCustomerV2(userId);
        setUser(user);
        const { application_fee_percentage, invoice_address: invoiceAddress, invoice_company_name: invoiceCompanyName, yard_free_days: yardFreeDays, yard_demurrage_rate: yardDemurrageRate, yard_per_diem_free_days: yardPerDiemFreeDays, yard_chassis_rate: yardChassisRate, yard_per_diem_rate: yardPerDiemRate, } = user;
        setYardFreeDays(yardFreeDays);
        const applicationFeePercentage = ((application_fee_percentage || DEFAULT_APPLICATION_FEE_PERCENTAGE) * 100).toFixed(2);
        setApplicationFeePercentage(applicationFeePercentage);
        setInvoiceAddress(invoiceAddress);
        setInvoiceCompanyName(invoiceCompanyName);
        setYardDemurrageRate(yardDemurrageRate);
        setYardPerDiemFreeDays(yardPerDiemFreeDays);
        setYardChassisRate(yardChassisRate);
        setYardPerDiemRate(yardPerDiemRate);
    }
    function handleYardFreeDaysChange(value) {
        setYardFreeDays(value);
    }
    function handleYardDemurrageRateChange(value) {
        setYardDemurrageRate(value);
    }
    function handleApplicationFeePercentageChange(value) {
        setApplicationFeePercentage(value);
    }
    function handleYardPerDiemFreeDays(value) {
        setYardPerDiemFreeDays(value);
    }
    function handleYardChassisRate(value) {
        setYardChassisRate(value);
    }
    function handleYardPerDiemRate(value) {
        setYardPerDiemRate(value);
    }
    function handleInvoiceCompanyNameChange(value) {
        setInvoiceCompanyName(value);
    }
    function handleInvoiceAddressChange(item) {
        setInvoiceAddress(item);
    }
    async function handleSave() {
        let application_fee_percentage = null;
        if (applicationFeePercentage) {
            application_fee_percentage = parseFloat(applicationFeePercentage) / 100;
        }
        const updates = {
            yard_free_days: yardFreeDays ? parseInt(yardFreeDays, 10) : null,
            invoice_company_name: invoiceCompanyName,
            invoice_address: invoiceAddress,
            application_fee_percentage,
            yard_demurrage_rate: yardDemurrageRate
                ? parseFloat(yardDemurrageRate)
                : null,
            yard_per_diem_free_days: yardPerDiemFreeDays,
            yard_chassis_rate: yardChassisRate ? parseFloat(yardChassisRate) : null,
            yard_per_diem_rate: yardPerDiemRate ? parseFloat(yardPerDiemRate) : null,
        };
        try {
            setSaving(true);
            await updateShipper(id, updates);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setSaving(false);
        }
    }
    useEffect(() => {
        const currentUser = SessionStore.getUser();
        if (!SessionStore.isAdmin() && currentUser._id !== id) {
            history.push(`/users/${currentUser._id}`);
        }
        else {
            fetchUser(id);
        }
    }, [id]);
    const renderSidebar = () => {
        return React.createElement(ShipperSettings, { userId: id, activePage: "yard-settings" });
    };
    const breadcrumbs = {
        pages: [
            {
                name: (user === null || user === void 0 ? void 0 : user.company_name) || t('SHIPPER'),
                href: `/users/${id}`,
                current: false,
            },
            {
                name: t('YARD_SETTINGS'),
                href: `/users/${id}/settings/yard`,
                current: true,
            },
        ],
    };
    return (React.createElement(LayoutWithSidebar, { sidebar: renderSidebar, breadcrumbs: breadcrumbs, className: "py-4" },
        React.createElement("div", { className: "mb-6" },
            React.createElement(Card, { className: "mb-6", header: () => React.createElement("h3", { className: "my-0" }, t('YARD')) },
                React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-4" },
                    React.createElement(CargomaticInput, { className: "mb-4", type: "number", name: "yard_last_free_days", label: t('YARD_FREE_DAYS'), info: t('SHIPPERS_FREE_YARD_DAYS'), value: yardFreeDays, placeholder: t('YARD_FREE_DAYS'), onChange: handleYardFreeDaysChange }),
                    React.createElement(CargomaticInput, { type: "number", step: 0.01, name: "yard_demurrage_rate", placeholder: t('YARD_DEMURRAGE_RATE'), label: t('YARD_DEMURRAGE_RATE'), value: yardDemurrageRate, onChange: handleYardDemurrageRateChange }),
                    React.createElement(CargomaticInput, { type: "number", step: 1, name: "application_fee_percentage", placeholder: `${t('ADMIN_FEE_PERCENTAGE')} (%)`, label: `${t('ADMIN_FEE_PERCENTAGE')} (%)`, value: applicationFeePercentage, onChange: handleApplicationFeePercentageChange })),
                React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-4 my-auto" },
                    React.createElement(CargomaticInput, { type: "number", name: "yard_per_diem_free_days", label: t('YARD_PER_DIEM_FREE_DAYS'), info: t('SHIPPERS_YARD_PER_DIEM_DAYS'), value: yardPerDiemFreeDays, placeholder: t('YARD_PER_DIEM_FREE_DAYS'), onChange: handleYardPerDiemFreeDays }),
                    React.createElement(CargomaticInput, { type: "number", name: "yard_chassis_rate", placeholder: t('YARD_CHASSIS_RATE'), label: t('YARD_CHASSIS_RATE'), value: yardChassisRate, onChange: handleYardChassisRate }),
                    React.createElement(CargomaticInput, { type: "number", name: "yard_per_diem_rate", placeholder: t('YARD_PER_DIEM_RATE'), label: t('YARD_PER_DIEM_RATE'), value: yardPerDiemRate, onChange: handleYardPerDiemRate })),
                React.createElement("hr", null),
                React.createElement("h4", { className: "mb-4" }, t('INVOICE_SETTINGS')),
                React.createElement("div", { className: "grid grid-cols-2 gap-4" },
                    React.createElement(CargomaticInput, { type: "text", name: "invoice_company_name", label: t('COMPANY_NAME'), className: "mb-4", placeholder: t('INVOICE_COMPANY_NAME'), value: invoiceCompanyName, onChange: handleInvoiceCompanyNameChange }),
                    React.createElement(CargomaticInput, { type: "location", name: "invoice_address", label: t('ADDRESS'), className: "mb-4", onSelect: handleInvoiceAddressChange, value: invoiceAddress === null || invoiceAddress === void 0 ? void 0 : invoiceAddress.address })))),
        React.createElement("div", { className: "flex justify-end mt-6" },
            React.createElement("button", { type: "button", className: "button button-green", onClick: handleSave },
                !saving && React.createElement("span", null, t('SAVE')),
                saving && React.createElement(LoadingCircle, { text: `${t('SAVING')}...` })))));
}
